import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { GermanDateAdapter } from './utils/german-date-adapter';

const GERMAN_DATE_FORMATS = {
  parse: { dateInput: { year: 'numeric', month: '2-digit', day: '2-digit' } },
  display: {
    dateInput: { year: 'numeric', month: '2-digit', day: '2-digit' },
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
  },
};

// Routing
import { AppRoutingModule } from './app-routing.module';

// Root Component
import { AppComponent } from './app.component';

// Core Module (singleton services, guards, interceptors)
import { CoreModule } from './core/core.module';

// Shared Module (common components, directives, pipes)
import { SharedModule } from './shared/shared.module';

// NgRx State Management
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { rootReducers, metaReducers } from './core/state';
import { AuditEffects } from './features/audits/state/audit.effects';

// Translation
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

// Auth0
import { provideAuth0 } from '@auth0/auth0-angular';

// API Configuration
import { ApiModule, Configuration } from './api';
import { environment } from '../assets/env/environment';

// Public Pages (eagerly loaded - part of core app shell)
import { LoginComponent } from './pages/login/login.component';
import { RegistrationRedirectComponent } from './pages/registration-redirect/registration-redirect.component';
import { ImpressumComponent } from './pages/impressum/impressum.component';
import { TermsComponent } from './pages/terms/terms.component';
import { PrivacyComponent } from './pages/privacy/privacy.component';

// Payment Module (eagerly loaded - dialogs used across features)
import { PaymentModule } from './features/payment/payment.module';

import { AuthConfig } from '@auth0/auth0-angular';
import { BrandingService } from './services/branding-service/branding.service';
import { HelpSidenavComponent } from './components/help-sidenav/help-sidenav.component';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

function auth0Config(): AuthConfig {
  return {
    domain: environment.AUTH_DOMAIN,
    clientId: environment.AUTH_CLIENT_ID,
    cacheLocation: 'localstorage',
    useRefreshTokens: true,
    authorizationParams: {
      redirect_uri: window.location.origin,
      audience: environment.AUTH_AUDIENCE,
      scope: 'read:form openid profile email',
      ui_locales: 'de',
    },
  };
}

@NgModule({
  declarations: [
    // Root component
    AppComponent,

    // Public pages (always loaded)
    LoginComponent,
    RegistrationRedirectComponent,
    ImpressumComponent,
    TermsComponent,
    PrivacyComponent,
    HelpSidenavComponent,
  ],
  imports: [
    // Angular
    BrowserModule,
    HttpClientModule,

    // Core & Shared
    CoreModule,
    SharedModule,

    // NgRx Store
    StoreModule.forRoot(rootReducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
      },
    }),
    EffectsModule.forRoot([AuditEffects]),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: !isDevMode(),
    }),

    // Translation
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),

    // API Configuration
    // Kein Token hier: den setzt TokenInterceptor frisch je Request (DOCU-1323).
    ApiModule.forRoot(() => {
      return new Configuration({
        basePath: environment.API_BASE_URL,
        withCredentials: false,
      });
    }),

    // Payment (eagerly loaded - dialogs used across app)
    PaymentModule,

    // Routing (must be last)
    AppRoutingModule,
  ],
  providers: [
    { provide: DateAdapter, useClass: GermanDateAdapter },
    { provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
    { provide: MAT_DATE_FORMATS, useValue: GERMAN_DATE_FORMATS },
    provideAnimationsAsync(),
    provideAuth0(auth0Config()),
    {
      provide: APP_INITIALIZER,
      useFactory: (branding: BrandingService) => () => branding.load(),
      deps: [BrandingService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
